import { useRef, useState } from 'react';
import { COLORS, BORDER_RADIUS, SHADOWS } from '../../styles/UISystem';
import { ButtonRounded } from '../UISystem/Buttons';
import { uploadProfilePhoto } from '../../services/storage/userDataStorage';
import { getProfileUserId, isValidProfileUserId } from '../../utils/identityUtils';
import { uploadPickedPhoto } from '../../services/storage/photoUpload';

/**
 * PhotoSelectionOverlay Component
 *
 * Two-button design:
 * - "Add photo" (Primary) - triggers native iOS picker (Photo Library, Take Photo, Choose File)
 * - "Saved" (Blank) - reveals grid of preset photos
 *
 * Tapping outside dismisses the overlay
 */
const PhotoSelectionOverlay = ({ onSelect, onClose, currentPhotoUrl }) => {
  const fileInputRef = useRef(null);
  const [showSavedPhotos, setShowSavedPhotos] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  // List of available profile photos
  const availablePhotos = [
    '/images/profiles/glasses.jpg',
    '/images/profiles/hepburn.jpg',
    '/images/profiles/mark.jpg',
    '/images/profiles/markley.jpg',
    '/images/profiles/mcqueen.jpg',
    '/images/profiles/prince.jpg',
    '/images/profiles/wonder.jpg',
    '/images/profiles/ziggy.jpg',
  ];

  // Handle preset photo selection - upload to Supabase for consistency with user uploads
  const handlePhotoSelect = async (localPhotoUrl) => {
    setIsUploading(true);

    try {
      // Use existing ID if valid, otherwise temp ID (first-time profile, not yet committed)
      const existingId = getProfileUserId();
      const profileUserId =
        existingId && isValidProfileUserId(existingId)
          ? existingId
          : `temp_${crypto.randomUUID().slice(0, 8)}`;

      // Fetch the local image and convert to blob
      console.log('[PHOTO_UPLOAD] Fetching preset image:', localPhotoUrl);
      const response = await fetch(localPhotoUrl);
      const blob = await response.blob();

      // Upload to Supabase
      console.log('[PHOTO_UPLOAD] Uploading preset to Supabase Storage...');
      const storageUrl = await uploadProfilePhoto(profileUserId, blob);

      if (storageUrl) {
        console.log('[PHOTO_UPLOAD] Preset upload successful:', storageUrl);
        onSelect(storageUrl);
        onClose();
        return;
      }

      // Fallback to local URL if upload fails
      console.warn('[PHOTO_UPLOAD] Preset upload failed, using local URL');
      onSelect(localPhotoUrl);
      onClose();
    } catch (error) {
      console.error('[PHOTO_UPLOAD] Error uploading preset:', error);
      // Fallback to local URL
      onSelect(localPhotoUrl);
      onClose();
    } finally {
      setIsUploading(false);
    }
  };

  // Compress + upload (and the base64 fallback) live in
  // services/storage/photoUpload.uploadPickedPhoto — one home for "picked file
  // → hosted URL", shared with the QR photo sheet.
  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    const photoUrl = await uploadPickedPhoto(file);
    if (!photoUrl) {
      alert('Error processing image. Please try a different photo.');
      return;
    }
    onSelect(photoUrl);
    onClose();
  };

  // Trigger file input click
  const handleAddNewPhoto = () => {
    fileInputRef.current?.click();
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.3)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: BORDER_RADIUS.PILL,
          padding: '24px',
          maxWidth: '320px',
          width: '90%',
          maxHeight: '80vh',
          overflowY: 'auto',
          position: 'relative',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            fontSize: '18px',
            fontWeight: '600',
            color: COLORS.DARK_GRAY,
            marginBottom: '20px',
            textAlign: 'center',
          }}
        >
          {isUploading ? 'Uploading...' : 'Select Profile Photo'}
        </div>

        {/* Add photo Button - Primary */}
        <div style={{ marginBottom: '12px' }}>
          <ButtonRounded
            text="Add photo"
            size="full"
            onClick={handleAddNewPhoto}
            disabled={isUploading}
          />
        </div>

        {/* Saved Button - Blank */}
        <ButtonRounded
          text="Saved"
          variant="blank"
          size="full"
          onClick={() => setShowSavedPhotos(!showSavedPhotos)}
        />

        {/* Photo Grid - shown when Saved Photos is clicked */}
        {showSavedPhotos && (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: '12px',
              marginTop: '16px',
            }}
          >
            {availablePhotos.map((photoUrl, index) => (
              <div
                key={index}
                onClick={() => !isUploading && handlePhotoSelect(photoUrl)}
                style={{
                  width: '100%',
                  paddingBottom: '100%',
                  position: 'relative',
                  opacity: isUploading ? 0.5 : 1,
                  cursor: 'pointer',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  border: currentPhotoUrl === photoUrl ? '3px solid #1C1C1E' : '2px solid #E5E5E5',
                  boxShadow: currentPhotoUrl === photoUrl ? SHADOWS.TIER2 : 'none',
                  transition: 'all 0.2s ease',
                }}
              >
                <img
                  src={photoUrl}
                  alt={`Profile option ${index + 1}`}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />
              </div>
            ))}
          </div>
        )}

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileSelect}
          style={{ display: 'none' }}
        />
      </div>
    </div>
  );
};

export default PhotoSelectionOverlay;
